import React, { useEffect, useState } from "react";
import { Upload, Loader2, CheckCircle2, AlertTriangle, FileUp, History, Download } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, SectionCard, StatCard, EmptyState } from "@/components/common";
import { Btn, Select } from "@/components/DataTable";

const ENTITIES = [["clients", "Clients"], ["sites", "Sites"], ["consultants", "Consultants"], ["contacts", "Contacts"], ["contracts", "Contracts"], ["historical_visits", "Historical Visits"]];

export default function ImportData() {
  const [entity, setEntity] = useState("clients");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/import/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/import/template?entity=${entity}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${entity}_template.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const doPreview = async () => {
    if (!file) return toast.error("Choose a file first");
    setBusy(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("entity", entity); fd.append("file", file);
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const commit = async () => {
    const rows = preview.rows.filter((r) => r.valid).map((r) => r.data);
    if (!rows.length) return toast.error("No valid rows to import");
    setBusy(true);
    try { const { data } = await api.post("/import/commit", { entity, rows }); toast.success(`Imported ${data.inserted} ${entity}`); setPreview(null); setFile(null); loadHistory(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Data Import" subtitle="Bulk-load clients, sites, consultants and contacts from Excel or CSV" icon={FileUp} />
      <div className="grid lg:grid-cols-3 gap-5">
        <SectionCard title="Upload" className="lg:col-span-1">
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Import type</label>
              <Select value={entity} onChange={(e) => { setEntity(e.target.value); setPreview(null); }} className="mt-1" data-testid="import-entity">
                {ENTITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </div>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-8 cursor-pointer hover:border-seq-green" data-testid="import-file-drop">
              <Upload size={28} className="text-slate-400" />
              <span className="text-sm text-slate-500">{file ? file.name : "Click to choose .csv or .xlsx"}</span>
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file-input" />
            </label>
            <Btn onClick={doPreview} disabled={busy || !file} className="w-full" data-testid="import-preview-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />} Preview import
            </Btn>
            <button onClick={downloadTemplate} data-testid="download-template-btn" className="w-full text-sm text-seq-green hover:underline flex items-center justify-center gap-1.5">
              <Download size={14} /> Download {entity.replace("_", " ")} template
            </button>
            <p className="text-xs text-slate-400">Column headers are matched flexibly (e.g. "Client Name", "Company", "Email"). Invalid rows are never imported. CSV and Excel (.xlsx) both supported.</p>
          </div>
        </SectionCard>

        <div className="lg:col-span-2 space-y-4">
          {preview && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Rows" value={preview.summary.total} />
                <StatCard label="Valid" value={preview.summary.valid} tone="green" icon={CheckCircle2} />
                <StatCard label="Invalid" value={preview.summary.invalid} tone={preview.summary.invalid ? "red" : "default"} icon={AlertTriangle} />
                <StatCard label="Duplicates" value={preview.summary.duplicates} tone={preview.summary.duplicates ? "amber" : "default"} />
              </div>
              <SectionCard title="Preview" noPad action={<Btn onClick={commit} disabled={busy || !preview.summary.valid} data-testid="import-commit-btn"><CheckCircle2 size={15} /> Import {preview.summary.valid} valid</Btn>}>
                <div className="overflow-x-auto max-h-[50vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                      <tr><th className="px-3 py-2 text-left font-medium">Status</th>{preview.columns.map((c) => <th key={c} className="px-3 py-2 text-left font-medium">{c}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {preview.rows.map((r, i) => (
                        <tr key={i} className={r.valid ? "" : r.duplicate ? "bg-amber-50/50" : "bg-red-50/40"}>
                          <td className="px-3 py-2">
                            {r.valid ? <span className="text-emerald-600 flex items-center gap-1 text-xs"><CheckCircle2 size={13} /> Valid</span>
                              : r.duplicate ? <span className="text-amber-600 text-xs">Duplicate</span>
                              : <span className="text-red-600 text-xs" title={r.errors.join(", ")}>{r.errors[0]}</span>}
                          </td>
                          {preview.columns.map((c) => <td key={c} className="px-3 py-2 text-slate-600">{r.data[c] || "—"}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </>
          )}
          {!preview && (
            <SectionCard title="Import history" noPad>
              {history.length ? (
                <div className="divide-y divide-slate-100">
                  {history.map((h) => (
                    <div key={h.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="text-slate-700"><b>{h.count}</b> {h.entity} imported by {h.imported_by}</span>
                      <span className="text-xs text-slate-400">{fmtDateTime(h.date)}</span>
                    </div>
                  ))}
                </div>
              ) : <EmptyState icon={History} title="No imports yet" hint="Upload a file to preview and import records." />}
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}
